import React, { useEffect, useMemo, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import { BankIcon } from "../../../assets/icons";
import { useParams, useLocation } from "react-router-dom";
import {
  reqToAddBranch,
  reqToGetBankBranchDetails,
  reqToGetBankDetails,
  updateBranchStatus,
  reqToUpdateBranch,
} from "../../../redux/slice/bankSlice";
import { useLoader } from "../../../context/LoaderContext";
import { useDispatch, useSelector } from "react-redux";
import { BankIcons } from "../../../utils/otherHelopers";
import DetailList from "./DetailList";
import { useToast } from "../../../context/TostContext";
import ConfirmModal from "../../../components/withModal/ConfirmModal";
import { usePermission } from "../../../hooks/usePermission";
import { TabCard } from "../../../components/custom-card/Cards";
import BranchModal from "./BranchModal";

const MODULE_SLUG = "banks";

export default function ViewBankDetails() {
  const { setLoading } = useLoader();
  const dispatch = useDispatch();
  const params = useParams();
  const location = useLocation();
  const id = location?.state?.id || params?.id;
  const { hasPermission } = usePermission();

  const canCreate = hasPermission(MODULE_SLUG, "create");
  const canUpdate = hasPermission(MODULE_SLUG, "update");
  const canDelete = hasPermission(MODULE_SLUG, "delete");
  const toast = useToast();

  // Add Branch Modal
  const [openAddBranchModal, setOpenAddBranchModal] = useState(false);
  // Search Bar
  const [detailSearchBar, setDetailSearchBar] = useState(() => {
    return sessionStorage.getItem("bankDetails_searchTerm") || "";
  });
  // Pagination
  const [limit, setLimit] = useState(() => {
    const savedLimit = sessionStorage.getItem("bankDetails_limit");
    return savedLimit ? JSON.parse(savedLimit) : { value: 10, label: 10 };
  });

  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = sessionStorage.getItem("bankDetails_page");
    return savedPage ? JSON.parse(savedPage) : 1;
  });

  const isInitialLoad = useRef(true);

  // Status Modal
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [modalItem, setModalItem] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [editBranchData, setEditBranchData] = useState(null);

  const details = useSelector((state) => state?.bank?.bankDetails?.data);
  const totalBranch = useSelector(
    (state) => state?.bank?.bankDetails?.data?.total_branches,
  );
  const branches = useSelector(
    (state) => state?.bank?.bankBranchDetails?.data || [],
  );
  const totalBranches = useSelector(
    (state) => state?.bank?.bankBranchDetails?.meta?.total || 0,
  );
  const isBranchLoading = useSelector((state) => state?.bank?.isBranchLoading);



  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      if (isInitialLoad.current) setLoading(true);

      sessionStorage.setItem("bankDetails_searchTerm", detailSearchBar);
      sessionStorage.setItem("bankDetails_limit", JSON.stringify(limit));
      sessionStorage.setItem("bankDetails_page", currentPage.toString());

      try {
        await Promise.all([
          dispatch(reqToGetBankDetails({ id })),
          dispatch(
            reqToGetBankBranchDetails({
              id,
              search: detailSearchBar,
              page: currentPage,
              limit: limit?.value,
            }),
          ),
        ]);
      } finally {
        if (isInitialLoad.current) setLoading(false);
        isInitialLoad.current = false;
      }
    };

    if (isInitialLoad.current) {
      fetchData();
    } else {
      const timer = setTimeout(fetchData, 500);
      return () => clearTimeout(timer);
    }
  }, [id, detailSearchBar, currentPage, limit, dispatch, setLoading]);

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!id) {
      toast.error("Bank Id not found!");
      return;
    }

    const data = {
      ...(!isEditMode && { bank_id: id }),
      name: values?.branch_name,
      city: values?.branch_city,
    };

    setLoading(true);
    try {
      let result;
      if (isEditMode) {
        result = await dispatch(
          reqToUpdateBranch({ id: editBranchData.id, data }),
        ).unwrap();
      } else {
        result = await dispatch(reqToAddBranch({ data })).unwrap();
      }

      if (result?.success === true) {
        toast.success(
          result?.message ||
          (isEditMode
            ? "Branch updated successfully!"
            : "Branch added successfully!"),
        );
        setSubmitting(false);
        dispatch(
          reqToGetBankBranchDetails({
            id,
            page: currentPage,
            limit: limit?.value,
          }),
        );
        handleCloseAddBranchModal();
        setLoading(false);
      }
    } catch (err) {
      toast.error(err?.message || (typeof err === 'string' ? err : "Failed to process request!"));
      setLoading(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusToggle = (item) => {
    setModalItem(item);
    setShowStatusModal(true);
  };

  const confirmStatusChange = () => {
    if (!modalItem) return;

    setLoading(true);
    dispatch(
      updateBranchStatus({
        id: modalItem.id,
        is_active: !modalItem.is_active,
      }),
    )
      .unwrap()
      .then((res) => {
        if (res?.success) {
          toast.success(res?.message || "Status updated successfully!");
          dispatch(
            reqToGetBankBranchDetails({
              id,
              page: currentPage,
              limit: limit?.value,
            }),
          );
          setShowStatusModal(false);
        } else {
          toast.error(res?.message || "Failed to update status!");
        }
      })
      .catch((err) => {
        toast.error(err?.message || (typeof err === 'string' ? err : "Failed to update status!"));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleEdit = (item) => {
    setEditBranchData(item);
    setIsEditMode(true);
    setIsViewMode(false);
    setOpenAddBranchModal(true);
  };

  const handleView = (item) => {
    setEditBranchData(item);
    setIsEditMode(false);
    setIsViewMode(true);
    setOpenAddBranchModal(true);
  };

  const handleCloseAddBranchModal = () => {
    setOpenAddBranchModal(false);
    setIsEditMode(false);
    setIsViewMode(false);
    setEditBranchData(null);
  };


  const paginationConfig = useMemo(() => ({
    currentPage,
    totalPages: Math.ceil(totalBranches / limit?.value) || 1,
    total: totalBranches || 0,
    onPageChange: (page) => setCurrentPage(page),
    pageSize: limit?.value,
    setPageSize: (size) => setLimit({ value: size, label: size }),
    setCurrentPage
  }), [currentPage, totalBranches, limit]);

  return (
    <>

      <Container fluid className="px-0">

        <div className="grid gap-4">

          <TabCard
            count={totalBranch}
            title={details?.name ? details?.name : "-"}
            image={details?.logo?.url}
            className="g-col-4 g-col-xxl-3"
            icon={BankIcons(details?.name)}
            iconClassName="icon-primary"
            // active={item.status === cardStatus}
            status="pending"
          // onClick={handleCardClick}
          />
          <TabCard
            count={totalBranch}
            title="Total Branch"
            className="g-col-4 g-col-xxl-3"
            icon={<BankIcon />}
            iconClassName="icon-secondary"
            // active={item.status === cardStatus}
            status="pending"
          // onClick={handleCardClick}
          />
        </div>
        <div className="bg-white rounded-2 overflow-hidden common-shadow mt-4">
          <DetailList
            data={branches}
            isLoading={isBranchLoading}
            isInitialLoad={isInitialLoad.current}
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            limit={limit?.value}
            detailSearchBar={detailSearchBar}
            setDetailSearchBar={setDetailSearchBar}
            setOpenAddBranchModal={setOpenAddBranchModal}
            handleStatusToggle={handleStatusToggle}
            handleEdit={handleEdit}
            handleView={handleView}
            canCreate={canCreate}
            canUpdate={canUpdate}
            canDelete={canDelete}
            pagination={paginationConfig}
          />
        </div>
      </Container>
      <BranchModal
        isOpen={openAddBranchModal}
        onClose={handleCloseAddBranchModal}
        title={isViewMode ? "View Branch Details" : isEditMode ? "Update Branch" : "Create New Branch"}
        isViewMode={isViewMode}
        isEditMode={isEditMode}
        editBranchData={editBranchData}
        onSubmit={handleSubmit}
      />

      {/* Status Confirmation Modal */}
      <ConfirmModal
        isOpen={showStatusModal}
        onClose={() => {
          setShowStatusModal(false);
          setModalItem(null);
        }}
        title={modalItem?.is_active ? "Deactivate User" : "Activate User"}
        message={
          modalItem?.is_active
            ? "Do you want to inactive?"
            : "Do you want to active?"
        }
        onConfirm={confirmStatusChange}
      />
    </>
  );
}
